import { useForm } from "@tanstack/react-form";
import { useEffect } from "react";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import { InfoBox } from "../../../components/InfoBox";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { SalesExportErrorCode } from "@KassaroAB/shared/types/enums/SalesExportErrorCode";
import { useTranslation } from "react-i18next";
import type { RestIntegration } from "@KassaroAB/shared/types/integrations/integration";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { formatDateTime } from "../../../utils/dateUtils";
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const HelpText = styled.div`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

type RestIntegrationPersonalkollen = Extract<
  RestIntegration,
  { type: IntegrationType.PERSONALKOLLEN }
>;

interface PersonalkollenFormProps {
  storeId: StoreId;
  integration?: RestIntegrationPersonalkollen;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const PersonalkollenForm = ({
  storeId,
  integration,
  onSuccess,
  onError,
  onCancel,
}: PersonalkollenFormProps) => {
  const { t } = useTranslation();
  const { data: store } = useGetStoreQuery(storeId);
  const { mutate: createMutation } = useCreateIntegrationMutation(
    store?.chainId ?? null,
    storeId,
  );
  const { mutate: updateMutation } = useUpdateIntegrationMutation(
    store?.chainId ?? null,
  );
  const exportError = integration?.exportError;

  const form = useForm({
    defaultValues: {
      name:
        integration?.name ||
        (store
          ? t("integrationDetails.personalkollenDefaultName", { storeName: store.name })
          : "Personalkollen"),
      apiKey: "",
    },
    onSubmit: ({ value }) => {
      if (!store?.chainId) {
        onError();
        return;
      }
      const apiKey = value.apiKey.trim();
      if (integration) {
        updateMutation(
          {
            integrationId: integration.id,
            integration: {
              type: IntegrationType.PERSONALKOLLEN as const,
              name: value.name,
              storeId: integration.storeId ?? storeId,
              ...(apiKey && { apiKey }),
            },
          },
          { onSuccess: () => onSuccess(), onError: () => onError() },
        );
        return;
      }
      createMutation(
        {
          type: IntegrationType.PERSONALKOLLEN as const,
          name: value.name,
          apiKey,
          storeId,
        },
        { onSuccess: () => onSuccess(), onError: () => onError() },
      );
    },
  });

  useEffect(() => {
    if (store && !integration) {
      form.setFieldValue(
        "name",
        t("integrationDetails.personalkollenDefaultName", { storeName: store.name }),
      );
    }
  }, [store, integration, form, t]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {exportError && (
        <InfoBox variant="error">
          {t(`integrationDetails.personalkollenExportError.${exportError.code}`, {
            date: formatDateTime(exportError.occurredAt),
          })}
          {exportError.code === SalesExportErrorCode.INVALID_DATA && (
            <HelpText>{exportError.message}</HelpText>
          )}
        </InfoBox>
      )}

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.nameRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel htmlFor="personalkollen-name">
              {t("integrationDetails.nameLabel")}
            </FormLabel>
            <Input
              id="personalkollen-name"
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.namePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="apiKey"
        validators={{
          onChange: ({ value }) => {
            if (integration) return undefined;
            return !value.trim() ? t("integrationDetails.apiKeyRequired") : undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel htmlFor="personalkollen-api-key">
              {integration
                ? t("integrationDetails.personalkollenApiKeyLabel")
                : t("integrationDetails.personalkollenApiKeyLabelNew")}
            </FormLabel>
            <Input
              id="personalkollen-api-key"
              type="password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required={!integration}
              placeholder={t("integrationDetails.apiKeyPlaceholder")}
            />
            <HelpText>{t("integrationDetails.personalkollenApiKeyHelp")}</HelpText>
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit">{integration ? t("common.update") : t("common.create")}</Button>
      </FormActions>
    </Form>
  );
};
